/**
 * AnimatedValue Component
 *
 * Wrapper for numeric/text values that animates on changes.
 * Features:
 * - CSS transitions (300ms ease) on value changes
 * - Desktop ripple highlight (`useMediaQuery('(hover: hover)')` + `.highlight`)
 * - Touch / coarse pointer: solid fade only
 * - Respects prefers-reduced-motion
 * - Staggered animation priorities (P1: 0ms, P2: 100ms, P3: 200ms)
 * - Optional shared concurrency cap via DiyAnimationBudgetProvider (default max 3)
 *
 * @see Story 2.1: Delta Engine Wired to Q&A
 * @see Epic Story 2.6: AnimatedValue AC closure (tests + budget wiring)
 * @see Epic Story 2.7: SparkleIndicator timing — use `getAnimatedValueTransitionEndMs` so sparkles appear after this transition.
 * @see Epic Story 2.11: P1/P2/P3 **stagger** (0/100/200ms) + max-3 **concurrent** highlight ripples; call-site map in `ProjectGuideHeader` / `ProjectGuideStep`.
 *
 * Stagger = `transitionDelay` from `priority`. The shared budget does **not** re-order who gets a slot by priority: `acquire()` runs per instance in
 * **React child effect order** (roughly tree/DOM order). P1–P3 still *read* as “priority” because late-starting P3s use a 200ms delay and do not start the CSS transition before higher-priority surfaces in the same wave.
 */

import { useEffect, useRef, useState } from 'react'
import { Box, Text } from '@mantine/core'
import { useMediaQuery, useReducedMotion } from '@mantine/hooks'
import { useDiyAnimationBudget } from './DiyAnimationBudgetContext'
import styles from './AnimatedValue.module.css'
import type { CSSProperties } from 'react'

export interface AnimatedValueProps {
  /** The value to display */
  value: string | number
  /** Optional prefix (e.g., "$") */
  prefix?: string
  /** Optional suffix (e.g., " hours") */
  suffix?: string
  /** Animation priority for stagger (1=0ms, 2=100ms, 3=200ms) */
  priority?: 1 | 2 | 3
  /** Text size */
  size?: 'xs' | 'sm' | 'md' | 'lg'
  /** Font weight */
  fw?: number
  /** Text color */
  c?: string
  /** Additional class name */
  className?: string
}

const PRIORITY_DELAYS: Record<1 | 2 | 3, number> = {
  1: 0,
  2: 100,
  3: 200,
}

const ANIMATION_DURATION = 300

/** Buffer after CSS transition + delay before considering the value morph complete (matches internal timeout). */
const TRANSITION_END_BUFFER_MS = 50

/**
 * Time from value change until the AnimatedValue transition (duration + stagger + buffer) completes.
 * Use to defer SparkleIndicator visibility until the morph finishes.
 */
export function getAnimatedValueTransitionEndMs(priority: 1 | 2 | 3 = 1): number {
  return ANIMATION_DURATION + PRIORITY_DELAYS[priority] + TRANSITION_END_BUFFER_MS
}

export function AnimatedValue({
  value,
  prefix = '',
  suffix = '',
  priority = 1,
  size = 'sm',
  fw,
  c,
  className,
}: AnimatedValueProps) {
  const [isAnimating, setIsAnimating] = useState(false)
  const previousValueRef = useRef<string | number | undefined>(undefined)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const budget = useDiyAnimationBudget()
  const holdsBudgetRef = useRef(false)

  const prefersReducedMotion = useReducedMotion(false, { getInitialValueInEffect: false })
  const isHoverCapable = useMediaQuery('(hover: hover)', false, {
    getInitialValueInEffect: false,
  })

  const transitionDelay = PRIORITY_DELAYS[priority]

  useEffect(() => {
    if (previousValueRef.current !== undefined && previousValueRef.current !== value) {
      if (!prefersReducedMotion) {
        const allowed = budget ? budget.acquire() : true
        if (allowed) {
          if (budget) {
            holdsBudgetRef.current = true
          }
          setIsAnimating(true)

          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
          }

          timeoutRef.current = setTimeout(() => {
            setIsAnimating(false)
            if (holdsBudgetRef.current && budget) {
              budget.release()
              holdsBudgetRef.current = false
            }
          }, ANIMATION_DURATION + transitionDelay + TRANSITION_END_BUFFER_MS)
        }
      }
    }

    previousValueRef.current = value

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      if (holdsBudgetRef.current && budget) {
        budget.release()
        holdsBudgetRef.current = false
      }
    }
  }, [value, prefersReducedMotion, transitionDelay, budget])

  const displayValue = `${prefix}${value}${suffix}`

  const boxStyle: CSSProperties = prefersReducedMotion
    ? { transition: 'none' }
    : {
        transition: `all ${ANIMATION_DURATION}ms ease`,
        transitionDelay: `${transitionDelay}ms`,
        ...(isAnimating ? { animationDelay: `${transitionDelay}ms` } : {}),
      }

  return (
    <Box
      component="span"
      data-testid="animated-value"
      data-animate-surface={isHoverCapable ? 'ripple' : 'solid'}
      className={`${styles.wrapper} ${isAnimating ? styles.animating : ''} ${isAnimating && isHoverCapable ? styles.highlight : ''} ${className ?? ''}`}
      style={boxStyle}
    >
      <Text component="span" size={size} fw={fw} c={c}>
        {displayValue}
      </Text>
    </Box>
  )
}
